// src/components/browse/Browse.js

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../lib/supabaseClient";
import "./Browse.css";
import TermCard from "./TermCard";
import { parseCategories } from "./browseUtils";

export default function Browse() {
    const navigate = useNavigate();

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");

    const [query, setQuery] = useState("");
    const [category, setCategory] = useState("All");

    const loadApproved = async () => {
        setLoading(true);
        setMessage("");

        const { data, error } = await supabase
            .from("term_submissions")
            .select(
                "id, term, definition, category, tags, palia_pedia_u, created_at",
            )
            .eq("approved", true)
            .order("term", { ascending: true });

        setLoading(false);

        if (error) {
            setMessage(error.message);
            return;
        }

        setItems(data || []);
    };

    useEffect(() => {
        loadApproved();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const categories = useMemo(() => {
        const set = new Set();
        items.forEach((i) => {
            parseCategories(i.category, 10).forEach((c) => set.add(c));
        });
        return ["All", ...Array.from(set).sort((a, b) => a.localeCompare(b))];
    }, [items]);

    const filteredItems = useMemo(() => {
        const q = query.trim().toLowerCase();

        return items.filter((item) => {
            const itemCats = parseCategories(item.category, 10);
            const inCategory =
                category === "All" || itemCats.includes(category);
            if (!inCategory) return false;
            if (!q) return true;

            const term = (item.term || "").toLowerCase();
            const def = (item.definition || "").toLowerCase();
            const tags = (item.tags || "").toLowerCase();

            return term.includes(q) || def.includes(q) || tags.includes(q);
        });
    }, [items, query, category]);

    const openTerm = (id) => navigate(`/${id}`);

    return (
        <div className="browse-container">
            {/* Header/controls are a proper panel */}
            <div className="browse-panel">
                <div className="browse-header">
                    <h2>Browse Terms</h2>
                    <button
                        className="browse-secondary"
                        onClick={loadApproved}
                        disabled={loading}
                        type="button"
                    >
                        {loading ? "Refreshing…" : "Refresh"}
                    </button>
                </div>

                <p className="browse-subtext">
                    Showing <strong>{filteredItems.length}</strong> approved
                    terms.
                </p>

                <div className="browse-controls">
                    <input
                        className="browse-input"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search terms, definitions, tags…"
                        autoComplete="off"
                    />

                    <select
                        className="browse-select"
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                    >
                        {categories.map((c) => (
                            <option key={c} value={c}>
                                {c}
                            </option>
                        ))}
                    </select>
                </div>

                {message && <div className="browse-message">{message}</div>}
            </div>

            {/* State panels also get consistent spacing & box styling */}
            {loading ? (
                <div className="browse-panel">
                    <p className="browse-subtext">Loading…</p>
                </div>
            ) : filteredItems.length === 0 ? (
                <div className="browse-panel">
                    <p className="browse-subtext">No matches found.</p>
                </div>
            ) : (
                <div className="browse-grid">
                    {filteredItems.map((item) => (
                        <TermCard key={item.id} item={item} onOpen={openTerm} />
                    ))}
                </div>
            )}
        </div>
    );
}
